import {Component, OnInit} from '@angular/core';
import {SelectItem} from 'primeng/api';
import * as _ from 'lodash';
import {BaseClientComponent} from '../base-client.component';
import {AuditTrailService} from '../../services/audit-trail.service';
import {AuditUtilsService} from '../../services/audit-utils.service';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {CompanyService} from '../../generated/api/company.service';
import {AuditEntry} from '../../models/auditEntry';
import {AuditTrailFilterCriteria} from '../../models/filter/audit-trail-filter-criteria';

@Component({
  selector: 'app-audit-trail',
  templateUrl: './audit-trail.component.html',
  styleUrls: ['./audit-trail.component.scss']
})
export class AuditTrailComponent extends BaseClientComponent implements OnInit {
  auditEntries: AuditEntry[] = [];
  totalItems = 0;
  pageSize = 50;
  page = 1;
  first = 0;
  loading = false;
  loadError = false;
  isMckayUser = false;
  companyItems: SelectItem[] = [];
  actionItems: SelectItem[];
  auditTrailFilterCriteria: AuditTrailFilterCriteria;
  dateRange: string[] = null;
  cols: any;
  userChoicesFiltersDefaultCriteria = {
    'action': null,
    'companyUniqid': null,
    'dateFrom': null,
    'dateTo': null,
  };
  userChoicesActiveFilters = _.clone(this.userChoicesFiltersDefaultCriteria);
  private authenticationDone = false;

  constructor(private auditTrailService: AuditTrailService,
              public auditUtilsService: AuditUtilsService,
              public authenticationService: AuthenticationService,
              private companyService: CompanyService,
              private utilsService: UtilsService) {
    super();
    this.actionItems = [
      {label: 'Created', value: 'insert'},
      {label: 'Updated', value: 'update'},
      {label: 'Deleted', value: 'remove'},
      {label: 'Role assigned', value: 'associate'},
      {label: 'Role removed', value: 'dissociate'},
    ];
    this.cols = [
      {header: 'When', width: '13%'},
      {header: 'Record', width: '18%'},
      {header: 'Action', width: '11%'},
      {header: 'Changes', width: '40%'},
      {header: 'Changed by', width: '18%'},
    ];
  }

  ngOnInit() {
    this.userChoicesActiveFilters = this.utilsService.loadUserPref('auditTrailFilter', this.userChoicesFiltersDefaultCriteria);
    this.auditTrailFilterCriteria = new AuditTrailFilterCriteria(this.userChoicesActiveFilters);
    if (this.auditTrailFilterCriteria.dateFrom || this.auditTrailFilterCriteria.dateTo) {
      this.dateRange = [this.auditTrailFilterCriteria.dateFrom, this.auditTrailFilterCriteria.dateTo];
    }
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.isMckayUser = this.authenticationService.getUserCurrent().permissions.isMckayUser;
      if (this.isMckayUser) {
        this.getCompanies();
      }
      this.authenticationDone = true;
      this.getAuditTrail();
    });
    this.subscriptions.push(response$);
  }

  loadLazy(event: any) {
    this.pageSize = event.rows;
    this.first = event.first;
    this.page = (event.first / event.rows) + 1;
    if (!this.authenticationDone) {
      return;
    }
    setTimeout(() => this.getAuditTrail());
  }

  search() {
    this.page = 1;
    this.first = 0;
    this.auditTrailFilterCriteria.dateFrom = this.dateRange ? this.dateRange[0] : null;
    this.auditTrailFilterCriteria.dateTo = this.dateRange ? this.dateRange[1] : null;
    this.utilsService.saveUserData('auditTrailFilter', _.clone(this.auditTrailFilterCriteria));
    this.getAuditTrail();
  }

  searchOnCompleteDateRange() {
    if (this.dateRange && this.dateRange[0] && this.dateRange[1]) {
      this.search();
    }
  }

  resetFilters() {
    this.auditTrailFilterCriteria = new AuditTrailFilterCriteria(this.userChoicesFiltersDefaultCriteria);
    this.dateRange = null;
    this.search();
  }

  private getAuditTrail() {
    this.loading = true;
    const response$ = this.auditTrailService.getAuditTrail(this.auditTrailFilterCriteria, this.page, this.pageSize)
      .subscribe({
        next: auditTrailPage => {
          this.auditEntries = auditTrailPage.items;
          this.totalItems = auditTrailPage.total;
          this.loadError = false;
          this.loading = false;
        },
        error: () => {
          this.auditEntries = [];
          this.totalItems = 0;
          this.loadError = true;
          this.loading = false;
          this.utilsService.displayMessage('The audit trail could not be loaded', 'Please try again later.', 'error');
        }
      });
    this.subscriptions.push(response$);
  }

  private getCompanies() {
    const response$ = this.companyService.getCompanyCollection().subscribe(companies => {
      this.companyItems = companies
        .map(company => ({label: company.name, value: company.uniqid}))
        .sort((firstCompany, secondCompany) => firstCompany.label.localeCompare(secondCompany.label));
    });
    this.subscriptions.push(response$);
  }
}
